"use client";

import { useCallback, useEffect, useRef } from "react";
import type {
  CallWorkflowState,
  StopWorkflowOptions,
  WorkflowQueueItem,
  WorkflowReducerState,
  WorkflowSessionSummaryStats,
} from "@/lib/modules/calls/workflow/types";

/**
 * Main-window-to-main-window BroadcastChannel so every CRM tab can share one
 * autodialer session. The owner tab broadcasts state; secondary tabs relay
 * skip/advance/start-call into the owner. Mute/hang-up go to the popup
 * channel instead — see autodialerCallChannel.
 */

export const AUTODIALER_PRIMARY_HEARTBEAT_MS = 15_000;
export const AUTODIALER_PRIMARY_SILENCE_MS = 30_000;
export const AUTODIALER_CLAIM_JITTER_MS = 200;
export const AUTODIALER_SYNC_DEBOUNCE_MS = 150;
export const AUTODIALER_RELAY_RETRY_MS = 500;
/** How long a newly-joined non-owner tab waits for a WHO_IS_PRIMARY reply
 * before assuming it's the only tab and claiming ownership outright — far
 * shorter than AUTODIALER_PRIMARY_SILENCE_MS, which exists to detect a
 * crashed owner that used to be heartbeating, not to gate a brand new join. */
export const AUTODIALER_JOIN_PROBE_TIMEOUT_MS = 3_000;
/** How long a follower waits for the owner to ack a relayed action it needs
 * a real outcome for (e.g. stopWorkflow) before giving up and reporting
 * failure, rather than optimistically resolving before the owner acts. */
export const AUTODIALER_RELAY_AWAIT_TIMEOUT_MS = 10_000;
/** How long an un-acked relay keeps being re-posted before it is dropped.
 * Generous enough to outlast an ownership handover, bounded so an action no
 * owner will ever accept cannot be retried for the rest of the session. */
export const AUTODIALER_RELAY_MAX_AGE_MS = 30_000;

export const autodialerSessionChannelName = (userId: number): string =>
  `autodialer-session:${userId}`;

export type RelayedWorkflowAction =
  | { type: "skipTask"; taskId: number }
  | { type: "unskipTask"; taskId: number }
  | { type: "skipCurrentTask" }
  | { type: "skipCurrentAndAdvance"; taskId: number }
  | { type: "advanceQueue" }
  | { type: "advanceQueueAfterMistakeCancel" }
  | { type: "takeScheduledCallNow"; taskId: number }
  | { type: "continuePastScheduledPrompt" }
  | { type: "startVoiceCall" }
  | { type: "transitionState"; state: CallWorkflowState }
  | {
      type: "recordCompletedCallInSession";
      isConverted?: boolean;
      completedTaskIds?: number[];
      convertedDelta?: number;
      countsAsDial?: boolean;
    }
  | {
      type: "revertCompletedCallFromSession";
      wasConverted?: boolean;
      convertedDelta?: number;
      completedTaskIds?: number[];
      skippedTaskIds?: number[];
      countsAsDial?: boolean;
      removedScheduledTasks?: WorkflowQueueItem[];
    }
  | { type: "markCallLogSaved" }
  | { type: "incrementConverted" }
  | { type: "stopWorkflow"; options?: StopWorkflowOptions };

/**
 * Only ever populated for a relayed `stopWorkflow` — the owner is the only
 * tab that talks to the server, so this is how the summary/error it produced
 * makes it back to whichever tab actually requested the stop.
 */
export interface RelayedStopWorkflowPayload {
  summaryStats?: WorkflowSessionSummaryStats;
  errorMessage?: string;
}

export interface StopWorkflowResult {
  stopped: boolean;
  summaryStats?: WorkflowSessionSummaryStats;
  errorMessage?: string;
}

export type RelayedActionResult =
  | { status: "completed"; payload?: RelayedStopWorkflowPayload }
  | { status: "retryable_failure" }
  | { status: "terminal_failure"; payload?: RelayedStopWorkflowPayload };

export type RelayedActionHandlerResult = RelayedActionResult | void;
export type RelayedActionOutcome = "completed" | "rejected";

type RelayedActionMessage = Extract<
  AutodialerSessionMessage,
  { type: "RELAY_ACTION" }
>;

type PendingRelay = {
  message: RelayedActionMessage;
  queuedAtMs: number;
};

export interface RelayedActionSettlementStore {
  get: (sessionId: string, actionId: string) => RelayedActionOutcome | null;
  set: (
    sessionId: string,
    actionId: string,
    outcome: RelayedActionOutcome,
  ) => void;
  clearSession: (sessionId: string) => void;
}

export type AutodialerSessionMessage =
  | { type: "PRIMARY_ALIVE"; instanceId: string; claimedAt: number }
  | { type: "WHO_IS_PRIMARY"; instanceId: string }
  | { type: "CLAIMING_PRIMARY"; instanceId: string; claimedAt: number }
  | { type: "OWNER_RELINQUISHED"; instanceId: string }
  | { type: "SYNC_STATE"; instanceId: string; state: WorkflowReducerState }
  | {
      type: "RELAY_ACTION";
      instanceId: string;
      sessionId: string;
      actionId: string;
      action: RelayedWorkflowAction;
    }
  | {
      type: "RELAY_ACTION_ACK";
      instanceId: string;
      recipientInstanceId: string;
      actionId: string;
      outcome: RelayedActionOutcome;
      payload?: RelayedStopWorkflowPayload;
    }
  | {
      type: "SESSION_ENDED";
      instanceId: string;
      sessionId: string | null;
      payload?: RelayedStopWorkflowPayload;
    }
  | {
      type: "SYNC_PREP_CACHE";
      instanceId: string;
      studentId: number;
      data: unknown;
    };

export function createAutodialerSessionInstanceId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function shouldClaimPrimary({
  lastPrimaryAliveAtMs,
  nowMs,
  silenceMs = AUTODIALER_PRIMARY_SILENCE_MS,
}: {
  lastPrimaryAliveAtMs: number | null;
  nowMs: number;
  silenceMs?: number;
}): boolean {
  if (lastPrimaryAliveAtMs == null) {
    return false;
  }
  return nowMs - lastPrimaryAliveAtMs >= silenceMs;
}

export function shouldTakeOverAfterRelinquish({
  isOwner,
  primaryInstanceId,
  relinquishingInstanceId,
}: {
  isOwner: boolean;
  primaryInstanceId: string | null;
  relinquishingInstanceId: string;
}): boolean {
  return !isOwner && primaryInstanceId === relinquishingInstanceId;
}

/**
 * Guards state-sync and session-end against a message from a session this tab
 * has already moved past — BroadcastChannel delivery can lag a stop/restart,
 * and applying such a message would overwrite or clear the live session.
 * A null id on either side means "unknown", which stays permissive.
 */
export const isMessageForCurrentSession = ({
  messageSessionId,
  currentSessionId,
}: {
  messageSessionId: string | null;
  currentSessionId: string | null;
}): boolean =>
  messageSessionId == null ||
  currentSessionId == null ||
  messageSessionId === currentSessionId;

export function isPendingRelayExpired({
  queuedAtMs,
  nowMs,
  maxAgeMs = AUTODIALER_RELAY_MAX_AGE_MS,
}: {
  queuedAtMs: number;
  nowMs: number;
  maxAgeMs?: number;
}): boolean {
  return nowMs - queuedAtMs > maxAgeMs;
}

export const shouldClaimAfterOwnerProbe = ({
  isOwner,
  primaryInstanceId,
}: {
  isOwner: boolean;
  primaryInstanceId: string | null;
}): boolean => !isOwner && primaryInstanceId == null;

/**
 * Outcome to settle a still-pending relayed action's ack with when the
 * channel is torn down (SESSION_ENDED, or unmount) before the real ack
 * arrives. Reaching this point while a relayed `stopWorkflow` is still
 * outstanding IS the success signal for that stop — the owner only ends the
 * session, and this tab only stops listening, after its own stop already
 * went through. Anything else left pending has nothing left to act on.
 */
export function resolvePendingAckOutcomeOnTeardown(
  pendingActionType: RelayedWorkflowAction["type"] | undefined,
): RelayedActionOutcome {
  return pendingActionType === "stopWorkflow" ? "completed" : "rejected";
}

/**
 * Split-brain: the earlier claim wins. Equal timestamps fall back to
 * lexicographically smaller instanceId.
 */
export function shouldYieldClaim({
  localInstanceId,
  remoteInstanceId,
  localClaimedAt,
  remoteClaimedAt,
}: {
  localInstanceId: string;
  remoteInstanceId: string;
  localClaimedAt: number;
  remoteClaimedAt: number;
}): boolean {
  if (remoteClaimedAt !== localClaimedAt) {
    return remoteClaimedAt < localClaimedAt;
  }
  return remoteInstanceId < localInstanceId;
}

export function claimJitterMs(
  maxMs: number = AUTODIALER_CLAIM_JITTER_MS,
): number {
  return Math.floor(Math.random() * (maxMs + 1));
}

export function createRelayedActionExecutor({
  execute,
  isOwner,
  isCurrentSession,
  acknowledge,
  settlementStore = createRelayedActionSettlementStore(null),
}: {
  execute: (
    action: RelayedWorkflowAction,
  ) => RelayedActionHandlerResult | Promise<RelayedActionHandlerResult>;
  isOwner: () => boolean;
  isCurrentSession: (sessionId: string) => boolean;
  acknowledge: (
    message: RelayedActionMessage,
    outcome: RelayedActionOutcome,
    payload?: RelayedStopWorkflowPayload,
  ) => void;
  settlementStore?: RelayedActionSettlementStore;
}): {
  handle: (message: RelayedActionMessage) => Promise<void>;
} {
  const inFlightActionIds = new Set<string>();
  const settledActions = new Map<string, RelayedActionOutcome>();

  return {
    handle: async (message) => {
      const priorOutcome =
        settledActions.get(message.actionId) ??
        settlementStore.get(message.sessionId, message.actionId);
      if (priorOutcome) {
        _rememberSettledRelay(settledActions, message.actionId, priorOutcome);
        acknowledge(message, priorOutcome);
        return;
      }
      if (inFlightActionIds.has(message.actionId)) {
        return;
      }
      if (!isCurrentSession(message.sessionId)) {
        _rememberSettledRelay(settledActions, message.actionId, "rejected");
        acknowledge(message, "rejected");
        return;
      }

      inFlightActionIds.add(message.actionId);
      try {
        const result = await execute(message.action);
        if (result?.status === "retryable_failure") {
          return;
        }

        const outcome =
          result?.status === "terminal_failure" ? "rejected" : "completed";
        const payload =
          result?.status === "completed" ||
          result?.status === "terminal_failure"
            ? result.payload
            : undefined;
        settlementStore.set(message.sessionId, message.actionId, outcome);
        _rememberSettledRelay(settledActions, message.actionId, outcome);
        if (!isOwner()) {
          return;
        }
        acknowledge(message, outcome, payload);
      } catch {
        // Leave the action unsettled so the sender can retry with this or the
        // next owner.
      } finally {
        inFlightActionIds.delete(message.actionId);
      }
    },
  };
}

export async function routeStopWorkflowRequest({
  isOwner,
  options,
  relayAndAwait,
  stopAsOwner,
}: {
  isOwner: boolean;
  options?: StopWorkflowOptions;
  relayAndAwait: (action: RelayedWorkflowAction) => Promise<{
    outcome: RelayedActionOutcome;
    payload?: RelayedStopWorkflowPayload;
  }>;
  stopAsOwner: (options?: StopWorkflowOptions) => Promise<StopWorkflowResult>;
}): Promise<StopWorkflowResult> {
  if (!isOwner) {
    const { outcome, payload } = await relayAndAwait({
      type: "stopWorkflow",
      options,
    });
    return { stopped: outcome === "completed", ...payload };
  }
  return stopAsOwner(options);
}

export async function executeRelayedStopWorkflow(
  action: Extract<RelayedWorkflowAction, { type: "stopWorkflow" }>,
  stopAsOwner: (options?: StopWorkflowOptions) => Promise<StopWorkflowResult>,
): Promise<RelayedActionResult> {
  const result = await stopAsOwner(action.options);
  const payload: RelayedStopWorkflowPayload = {
    summaryStats: result.summaryStats,
    errorMessage: result.errorMessage,
  };
  // A failed stop (session already gone, a real server/network error) is
  // terminal, not retryable — matching relayed skip/unskip. Without this the
  // follower would wait on routeStopWorkflowRequest's ack while the owner
  // silently kept resending the same failing stop every relay-retry tick.
  return result.stopped
    ? { status: "completed", payload }
    : { status: "terminal_failure", payload };
}

export function useAutodialerSessionChannel({
  userId,
  enabled,
  isSessionOwner,
  setIsSessionOwner,
  state,
  onRelayedAction,
  onSessionEnded,
  onSyncedState,
  onPrepCache,
}: {
  userId: number | null;
  enabled: boolean;
  isSessionOwner: boolean;
  setIsSessionOwner: (owner: boolean) => void;
  state: WorkflowReducerState;
  onRelayedAction: (
    action: RelayedWorkflowAction,
  ) => RelayedActionHandlerResult | Promise<RelayedActionHandlerResult>;
  onSessionEnded: () => void;
  onSyncedState: (state: WorkflowReducerState) => void;
  onPrepCache?: (studentId: number, data: unknown) => void;
}): {
  relayAction: (action: RelayedWorkflowAction) => void;
  relayActionAndAwait: (action: RelayedWorkflowAction) => Promise<{
    outcome: RelayedActionOutcome;
    payload?: RelayedStopWorkflowPayload;
  }>;
  broadcastSessionEnded: (payload?: RelayedStopWorkflowPayload) => void;
  broadcastPrepCache: (studentId: number, data: unknown) => void;
  probeForOwner: () => void;
} {
  const instanceIdRef = useRef(createAutodialerSessionInstanceId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const isOwnerRef = useRef(isSessionOwner);
  const sessionIdRef = useRef(state.sessionId);
  const lastPrimaryAliveAtRef = useRef<number | null>(null);
  const claimTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ownerProbeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const localClaimedAtRef = useRef<number | null>(null);
  const primaryInstanceIdRef = useRef<string | null>(null);
  const pendingRelaysRef = useRef(new Map<string, PendingRelay>());
  const pendingAckResolversRef = useRef(
    new Map<
      string,
      (
        outcome: RelayedActionOutcome,
        payload?: RelayedStopWorkflowPayload,
      ) => void
    >(),
  );
  const relayedActionExecutorRef = useRef<ReturnType<
    typeof createRelayedActionExecutor
  > | null>(null);
  const settlementStoreRef = useRef<RelayedActionSettlementStore | null>(null);
  const onRelayedActionRef = useRef(onRelayedAction);
  const onSessionEndedRef = useRef(onSessionEnded);
  const onSyncedStateRef = useRef(onSyncedState);
  const onPrepCacheRef = useRef(onPrepCache);
  const setIsSessionOwnerRef = useRef(setIsSessionOwner);
  const scheduleClaimRef = useRef<() => void>(() => {});
  const drainPendingRelaysRef = useRef<() => void>(() => {});

  useEffect(() => {
    isOwnerRef.current = isSessionOwner;
    sessionIdRef.current = state.sessionId;
    // Stamp a claim time for ownership acquired outside promoteToOwner (e.g.
    // a freshly started workflow calling setIsSessionOwner(true) directly),
    // so the CLAIMING_PRIMARY handler always has a claimedAt to defend with.
    if (isSessionOwner) {
      if (localClaimedAtRef.current == null) {
        localClaimedAtRef.current = Date.now();
      }
    } else {
      localClaimedAtRef.current = null;
    }
    onRelayedActionRef.current = onRelayedAction;
    onSessionEndedRef.current = onSessionEnded;
    onSyncedStateRef.current = onSyncedState;
    onPrepCacheRef.current = onPrepCache;
    setIsSessionOwnerRef.current = setIsSessionOwner;
  });

  const clearClaimTimeout = useCallback(() => {
    if (claimTimeoutRef.current) {
      clearTimeout(claimTimeoutRef.current);
      claimTimeoutRef.current = null;
    }
  }, []);

  const post = useCallback((message: AutodialerSessionMessage) => {
    channelRef.current?.postMessage(message);
  }, []);

  const promoteToOwner = useCallback(() => {
    localClaimedAtRef.current = Date.now();
    isOwnerRef.current = true;
    primaryInstanceIdRef.current = instanceIdRef.current;
    setIsSessionOwnerRef.current(true);
    post({
      type: "CLAIMING_PRIMARY",
      instanceId: instanceIdRef.current,
      claimedAt: localClaimedAtRef.current,
    });
    post({
      type: "PRIMARY_ALIVE",
      instanceId: instanceIdRef.current,
      claimedAt: localClaimedAtRef.current,
    });

    // Let simultaneous claims settle before executing actions queued while
    // there was no owner. A loser will be demoted before this fires.
    setTimeout(() => {
      if (isOwnerRef.current) {
        drainPendingRelaysRef.current();
      }
    }, AUTODIALER_CLAIM_JITTER_MS + 1);
  }, [post]);

  const probeForOwner = useCallback(() => {
    if (!channelRef.current) {
      return;
    }
    // Conflict recovery can call this before React has propagated the
    // orchestration hook's demotion into this hook. Demote the channel ref
    // synchronously so the probe cannot be skipped and leave every tab idle.
    isOwnerRef.current = false;
    localClaimedAtRef.current = null;
    setIsSessionOwnerRef.current(false);
    if (ownerProbeTimeoutRef.current) {
      clearTimeout(ownerProbeTimeoutRef.current);
    }
    primaryInstanceIdRef.current = null;
    lastPrimaryAliveAtRef.current = Date.now();
    post({ type: "WHO_IS_PRIMARY", instanceId: instanceIdRef.current });
    ownerProbeTimeoutRef.current = setTimeout(() => {
      ownerProbeTimeoutRef.current = null;
      // After a version conflict, yield permanently instead of reclaiming.
      // BroadcastChannel cannot see another browser, so shouldClaimAfterOwnerProbe
      // would return true even when a live owner exists elsewhere, causing
      // conflict loops. The conflict itself proves another owner already won.
    }, AUTODIALER_JOIN_PROBE_TIMEOUT_MS);
    // Arm the 30s silence takeover so same-browser recovery works if the
    // Redis winner already closed or crashed.
    scheduleClaimRef.current();
  }, [post]);

  const drainPendingRelays = useCallback(() => {
    for (const [actionId, pending] of pendingRelaysRef.current) {
      if (pending.message.sessionId !== sessionIdRef.current) {
        pendingRelaysRef.current.delete(actionId);
        continue;
      }
      void relayedActionExecutorRef.current?.handle(pending.message);
    }
  }, []);

  useEffect(() => {
    drainPendingRelaysRef.current = drainPendingRelays;
  }, [drainPendingRelays]);

  // Re-armed every time we hear from an owner (see the message handlers
  // below) so we keep watching for silence for as long as we're not the
  // owner — a one-shot check here would only ever catch an owner that was
  // already silent before we joined.
  const scheduleClaim = useCallback(() => {
    if (isOwnerRef.current) {
      return;
    }
    if (claimTimeoutRef.current) {
      clearTimeout(claimTimeoutRef.current);
    }
    const lastAliveAtMs = lastPrimaryAliveAtRef.current;
    const elapsedMs =
      lastAliveAtMs == null ? Infinity : Date.now() - lastAliveAtMs;
    const dueInMs = Math.max(AUTODIALER_PRIMARY_SILENCE_MS - elapsedMs, 0);
    claimTimeoutRef.current = setTimeout(
      () => {
        claimTimeoutRef.current = null;
        if (isOwnerRef.current) {
          return;
        }
        if (
          !shouldClaimPrimary({
            lastPrimaryAliveAtMs: lastPrimaryAliveAtRef.current,
            nowMs: Date.now(),
          })
        ) {
          scheduleClaimRef.current();
          return;
        }
        promoteToOwner();
      },
      dueInMs + (dueInMs === 0 ? claimJitterMs() : 0),
    );
  }, [promoteToOwner]);

  useEffect(() => {
    scheduleClaimRef.current = scheduleClaim;
  });

  useEffect(() => {
    if (!enabled || userId == null || typeof BroadcastChannel === "undefined") {
      return;
    }

    const channel = new BroadcastChannel(autodialerSessionChannelName(userId));
    channelRef.current = channel;
    const pendingAckResolvers = pendingAckResolversRef.current;
    const pendingRelays = pendingRelaysRef.current;
    const settlementStore = createRelayedActionSettlementStore(
      window.localStorage,
    );
    settlementStoreRef.current = settlementStore;
    const relayedActionExecutor = createRelayedActionExecutor({
      execute: (action) => onRelayedActionRef.current(action),
      isOwner: () => isOwnerRef.current,
      isCurrentSession: (sessionId) => sessionId === sessionIdRef.current,
      acknowledge: (message, outcome, payload) => {
        if (message.instanceId === instanceIdRef.current) {
          pendingRelaysRef.current.delete(message.actionId);
          return;
        }
        post({
          type: "RELAY_ACTION_ACK",
          instanceId: instanceIdRef.current,
          recipientInstanceId: message.instanceId,
          actionId: message.actionId,
          outcome,
          payload,
        });
      },
      settlementStore,
    });
    relayedActionExecutorRef.current = relayedActionExecutor;
    // Treat join as "primary last seen now" so we wait a full silence window
    // unless this tab is already the owner (it will start heartbeats immediately).
    lastPrimaryAliveAtRef.current = Date.now();

    channel.onmessage = (event: MessageEvent<AutodialerSessionMessage>) => {
      const message = event.data;
      if (!message || message.instanceId === instanceIdRef.current) {
        return;
      }

      switch (message.type) {
        case "PRIMARY_ALIVE": {
          lastPrimaryAliveAtRef.current = Date.now();
          if (
            localClaimedAtRef.current != null &&
            !shouldYieldClaim({
              localInstanceId: instanceIdRef.current,
              remoteInstanceId: message.instanceId,
              localClaimedAt: localClaimedAtRef.current,
              remoteClaimedAt: message.claimedAt,
            })
          ) {
            // Stay owner — keep defending our existing claim time.
            break;
          }
          primaryInstanceIdRef.current = message.instanceId;
          localClaimedAtRef.current = null;
          if (isOwnerRef.current) {
            isOwnerRef.current = false;
            setIsSessionOwnerRef.current(false);
          }
          scheduleClaim();
          break;
        }
        case "WHO_IS_PRIMARY": {
          if (isOwnerRef.current) {
            post({
              type: "PRIMARY_ALIVE",
              instanceId: instanceIdRef.current,
              claimedAt: localClaimedAtRef.current ?? Date.now(),
            });
          }
          break;
        }
        case "CLAIMING_PRIMARY": {
          lastPrimaryAliveAtRef.current = Date.now();
          // The earlier claim always wins — this applies whether we're
          // already the owner (localClaimedAtRef is stamped the moment
          // ownership is acquired, however it was acquired) or mid-claim
          // ourselves. No recorded claim means we have nothing to defend.
          if (
            localClaimedAtRef.current != null &&
            !shouldYieldClaim({
              localInstanceId: instanceIdRef.current,
              remoteInstanceId: message.instanceId,
              localClaimedAt: localClaimedAtRef.current,
              remoteClaimedAt: message.claimedAt,
            })
          ) {
            break;
          }
          primaryInstanceIdRef.current = message.instanceId;
          localClaimedAtRef.current = null;
          if (isOwnerRef.current) {
            isOwnerRef.current = false;
            setIsSessionOwnerRef.current(false);
          }
          scheduleClaim();
          break;
        }
        case "OWNER_RELINQUISHED": {
          if (
            !shouldTakeOverAfterRelinquish({
              isOwner: isOwnerRef.current,
              primaryInstanceId: primaryInstanceIdRef.current,
              relinquishingInstanceId: message.instanceId,
            })
          ) {
            break;
          }
          primaryInstanceIdRef.current = null;
          lastPrimaryAliveAtRef.current =
            Date.now() - AUTODIALER_PRIMARY_SILENCE_MS;
          scheduleClaim();
          break;
        }
        case "SYNC_STATE": {
          if (
            !isMessageForCurrentSession({
              messageSessionId: message.state.sessionId,
              currentSessionId: sessionIdRef.current,
            })
          ) {
            break;
          }
          lastPrimaryAliveAtRef.current = Date.now();
          if (!isOwnerRef.current) {
            // Only a follower may treat the sender as primary. An owner that
            // adopted a demoted tab's in-flight sync here would point
            // primaryInstanceIdRef at a tab that no longer owns anything.
            primaryInstanceIdRef.current = message.instanceId;
            onSyncedStateRef.current(message.state);
            scheduleClaim();
          }
          break;
        }
        case "RELAY_ACTION": {
          if (isOwnerRef.current) {
            void relayedActionExecutor.handle(message);
          }
          break;
        }
        case "RELAY_ACTION_ACK": {
          if (message.recipientInstanceId === instanceIdRef.current) {
            pendingRelaysRef.current.delete(message.actionId);
            const resolve = pendingAckResolversRef.current.get(
              message.actionId,
            );
            if (resolve) {
              pendingAckResolversRef.current.delete(message.actionId);
              resolve(message.outcome, message.payload);
            }
          }
          break;
        }
        case "SESSION_ENDED": {
          if (
            !isMessageForCurrentSession({
              messageSessionId: message.sessionId,
              currentSessionId: sessionIdRef.current,
            })
          ) {
            break;
          }
          if (sessionIdRef.current) {
            settlementStore.clearSession(sessionIdRef.current);
          }
          // Settle before clearing pendingRelaysRef — _settleAllPendingAcks
          // looks up each pending action there to tell a still-awaited stop
          // (SESSION_ENDED is proof it succeeded) apart from anything else.
          // The owner posts SESSION_ENDED as soon as its local state clears,
          // which normally beats its own RELAY_ACTION_ACK for the same stop —
          // so the summary/error has to ride along on SESSION_ENDED itself,
          // not just the ack, or a follower-initiated stop never sees it.
          _settleAllPendingAcks(
            pendingAckResolversRef.current,
            pendingRelaysRef.current,
            message.payload,
          );
          pendingRelaysRef.current.clear();
          onSessionEndedRef.current();
          break;
        }
        case "SYNC_PREP_CACHE": {
          if (!isOwnerRef.current) {
            onPrepCacheRef.current?.(message.studentId, message.data);
          }
          break;
        }
      }
    };

    let joinProbeTimeout: ReturnType<typeof setTimeout> | null = null;
    const relayRetryInterval = setInterval(() => {
      _dropExpiredPendingRelays(pendingRelaysRef.current, Date.now());
      if (isOwnerRef.current) {
        drainPendingRelaysRef.current();
      } else {
        for (const pending of pendingRelaysRef.current.values()) {
          channel.postMessage(pending.message);
        }
      }
    }, AUTODIALER_RELAY_RETRY_MS);

    const relinquishOwnership = () => {
      if (isOwnerRef.current) {
        channel.postMessage({
          type: "OWNER_RELINQUISHED",
          instanceId: instanceIdRef.current,
        } satisfies AutodialerSessionMessage);
        isOwnerRef.current = false;
        localClaimedAtRef.current = null;
        primaryInstanceIdRef.current = null;
        setIsSessionOwnerRef.current(false);
      }
    };
    window.addEventListener("pagehide", relinquishOwnership);

    if (!isOwnerRef.current) {
      const joinedAtMs = lastPrimaryAliveAtRef.current;
      post({ type: "WHO_IS_PRIMARY", instanceId: instanceIdRef.current });
      joinProbeTimeout = setTimeout(() => {
        joinProbeTimeout = null;
        // If nothing has updated lastPrimaryAliveAtRef since we joined, no
        // other tab answered the probe — claim ownership outright instead of
        // waiting out the full silence window meant for a crashed owner.
        if (
          !isOwnerRef.current &&
          lastPrimaryAliveAtRef.current === joinedAtMs
        ) {
          promoteToOwner();
        }
      }, AUTODIALER_JOIN_PROBE_TIMEOUT_MS);
      scheduleClaim();
    }

    return () => {
      if (joinProbeTimeout) {
        clearTimeout(joinProbeTimeout);
      }
      if (ownerProbeTimeoutRef.current) {
        clearTimeout(ownerProbeTimeoutRef.current);
        ownerProbeTimeoutRef.current = null;
      }
      clearInterval(relayRetryInterval);
      clearClaimTimeout();
      _settleAllPendingAcks(pendingAckResolvers, pendingRelays);
      relinquishOwnership();
      window.removeEventListener("pagehide", relinquishOwnership);
      channel.close();
      if (channelRef.current === channel) {
        channelRef.current = null;
      }
      if (relayedActionExecutorRef.current === relayedActionExecutor) {
        relayedActionExecutorRef.current = null;
        settlementStoreRef.current = null;
      }
    };
  }, [clearClaimTimeout, enabled, post, promoteToOwner, scheduleClaim, userId]);

  useEffect(() => {
    if (!enabled || !isSessionOwner) {
      return;
    }
    post({
      type: "PRIMARY_ALIVE",
      instanceId: instanceIdRef.current,
      claimedAt: localClaimedAtRef.current ?? Date.now(),
    });
    const heartbeat = setInterval(() => {
      if (!isOwnerRef.current) {
        return;
      }
      post({
        type: "PRIMARY_ALIVE",
        instanceId: instanceIdRef.current,
        claimedAt: localClaimedAtRef.current ?? Date.now(),
      });
    }, AUTODIALER_PRIMARY_HEARTBEAT_MS);
    return () => clearInterval(heartbeat);
  }, [enabled, isSessionOwner, post]);

  useEffect(() => {
    if (!enabled || !isSessionOwner || !state.sessionId) {
      return;
    }
    const timer = setTimeout(() => {
      // Demotion lands on isOwnerRef synchronously but only reaches the
      // isSessionOwner prop on the next render, so a sync debounced just
      // before this tab lost ownership would otherwise still go out.
      if (!isOwnerRef.current) {
        return;
      }
      post({
        type: "SYNC_STATE",
        instanceId: instanceIdRef.current,
        state,
      });
    }, AUTODIALER_SYNC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [
    enabled,
    isSessionOwner,
    post,
    state,
    state.sessionId,
    state.state,
    state.queuePosition,
    state.currentTaskId,
    state.skippedSet,
    state.awaitingFirstCall,
    state.completedCount,
    state.convertedCount,
    state.sessionBaselineCompletedCount,
    state.sessionBaselineSkippedCount,
    state.sessionSkippedCount,
    state.queue,
    state.scheduledStack,
    state.scheduledPromptTask,
    state.callLogSaved,
    state.lastCallStartedAt,
    state.sessionVersion,
    state.completedTaskIds,
  ]);

  const relayAction = useCallback(
    (action: RelayedWorkflowAction) => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) {
        return;
      }
      // Starting a call is not additive the way a skip or an increment is —
      // a second click while the first relay is still unacked would have the
      // owner dial twice.
      if (
        action.type === "startVoiceCall" &&
        _hasPendingRelayOfType(pendingRelaysRef.current, "startVoiceCall")
      ) {
        return;
      }
      const actionId = createAutodialerSessionInstanceId();
      const message = {
        type: "RELAY_ACTION",
        instanceId: instanceIdRef.current,
        sessionId,
        actionId,
        action,
      } satisfies AutodialerSessionMessage;
      pendingRelaysRef.current.set(actionId, {
        message,
        queuedAtMs: Date.now(),
      });
      post(message);
    },
    [post],
  );

  const relayActionAndAwait = useCallback(
    (
      action: RelayedWorkflowAction,
    ): Promise<{
      outcome: RelayedActionOutcome;
      payload?: RelayedStopWorkflowPayload;
    }> => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) {
        return Promise.resolve({ outcome: "rejected" });
      }
      const actionId = createAutodialerSessionInstanceId();
      const message = {
        type: "RELAY_ACTION",
        instanceId: instanceIdRef.current,
        sessionId,
        actionId,
        action,
      } satisfies AutodialerSessionMessage;
      pendingRelaysRef.current.set(actionId, {
        message,
        queuedAtMs: Date.now(),
      });
      post(message);

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          pendingAckResolversRef.current.delete(actionId);
          // Drop the relay too. Leaving it queued keeps the retry interval
          // re-posting it, so an owner reachable later would execute an
          // action this tab has already reported as failed.
          pendingRelaysRef.current.delete(actionId);
          resolve({ outcome: "rejected" });
        }, AUTODIALER_RELAY_AWAIT_TIMEOUT_MS);
        pendingAckResolversRef.current.set(actionId, (outcome, payload) => {
          clearTimeout(timeout);
          resolve({ outcome, payload });
        });
      });
    },
    [post],
  );

  const broadcastSessionEnded = useCallback(
    (payload?: RelayedStopWorkflowPayload) => {
      if (sessionIdRef.current) {
        settlementStoreRef.current?.clearSession(sessionIdRef.current);
      }
      post({
        type: "SESSION_ENDED",
        instanceId: instanceIdRef.current,
        sessionId: sessionIdRef.current,
        payload,
      });
    },
    [post],
  );

  const broadcastPrepCache = useCallback(
    (studentId: number, data: unknown) => {
      post({
        type: "SYNC_PREP_CACHE",
        instanceId: instanceIdRef.current,
        studentId,
        data,
      });
    },
    [post],
  );

  return {
    relayAction,
    relayActionAndAwait,
    broadcastSessionEnded,
    broadcastPrepCache,
    probeForOwner,
  };
}

const RELAY_SETTLEMENT_STORAGE_PREFIX = "autodialer-relay-settlement:";

export const createRelayedActionSettlementStore = (
  storage: Storage | null,
): RelayedActionSettlementStore => {
  const fallback = new Map<string, RelayedActionOutcome>();
  const sessionPrefix = (sessionId: string): string =>
    `${RELAY_SETTLEMENT_STORAGE_PREFIX}${encodeURIComponent(sessionId)}:`;
  const keyFor = (sessionId: string, actionId: string): string =>
    `${sessionPrefix(sessionId)}${encodeURIComponent(actionId)}`;

  return {
    get: (sessionId, actionId) => {
      const key = keyFor(sessionId, actionId);
      try {
        const stored = storage?.getItem(key);
        if (stored === "completed" || stored === "rejected") {
          return stored;
        }
      } catch {
        // Fall back to owner-local memory if browser storage is unavailable.
      }
      return fallback.get(key) ?? null;
    },
    set: (sessionId, actionId, outcome) => {
      const key = keyFor(sessionId, actionId);
      fallback.set(key, outcome);
      try {
        storage?.setItem(key, outcome);
      } catch {
        // The fallback still prevents duplicates in the current owner.
      }
    },
    clearSession: (sessionId) => {
      const prefix = sessionPrefix(sessionId);
      for (const key of fallback.keys()) {
        if (key.startsWith(prefix)) {
          fallback.delete(key);
        }
      }
      if (!storage) {
        return;
      }
      try {
        const keysToDelete: string[] = [];
        for (let index = 0; index < storage.length; index += 1) {
          const key = storage.key(index);
          if (key?.startsWith(prefix)) {
            keysToDelete.push(key);
          }
        }
        for (const key of keysToDelete) {
          storage.removeItem(key);
        }
      } catch {
        // Session cleanup is best-effort; IDs are UUID-scoped and cannot
        // suppress actions in a later workflow session.
      }
    },
  };
};

const _rememberSettledRelay = (
  settledActions: Map<string, RelayedActionOutcome>,
  actionId: string,
  outcome: RelayedActionOutcome,
): void => {
  if (settledActions.size >= 500) {
    const oldestActionId = settledActions.keys().next().value;
    if (oldestActionId) {
      settledActions.delete(oldestActionId);
    }
  }
  settledActions.set(actionId, outcome);
};

const _hasPendingRelayOfType = (
  pendingRelays: Map<string, PendingRelay>,
  actionType: RelayedWorkflowAction["type"],
): boolean => {
  for (const pending of pendingRelays.values()) {
    if (pending.message.action.type === actionType) {
      return true;
    }
  }
  return false;
};

const _dropExpiredPendingRelays = (
  pendingRelays: Map<string, PendingRelay>,
  nowMs: number,
): void => {
  for (const [actionId, pending] of pendingRelays) {
    if (isPendingRelayExpired({ queuedAtMs: pending.queuedAtMs, nowMs })) {
      pendingRelays.delete(actionId);
    }
  }
};

const _settleAllPendingAcks = (
  resolvers: Map<
    string,
    (
      outcome: RelayedActionOutcome,
      payload?: RelayedStopWorkflowPayload,
    ) => void
  >,
  pendingRelays: Map<string, PendingRelay>,
  sessionEndedPayload?: RelayedStopWorkflowPayload,
): void => {
  for (const [actionId, resolve] of resolvers) {
    const pendingActionType = pendingRelays.get(actionId)?.message.action.type;
    resolve(
      resolvePendingAckOutcomeOnTeardown(pendingActionType),
      pendingActionType === "stopWorkflow" ? sessionEndedPayload : undefined,
    );
  }
  resolvers.clear();
};
